import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react'
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride'

const TOUR_DONE_KEY = 'ugc4you_packages_tour_done'

const steps = [
  {
    target: 'body',
    content: (
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Welkom bij Pakketten!</h2>
        <p className="text-base sm:text-lg mb-2">
          Stel maximaal <strong>3 pakketten</strong> samen die bedrijven direct kunnen boeken via je profiel.
        </p>
        <p className="text-gray-600">
          We laten je stap voor stap zien hoe het werkt.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="pkg-add"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Pakket aanmaken</h3>
        <p>
          Klik hier om je eerste pakket aan te maken. Je kunt tot 3 pakketten toevoegen.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="pkg-name-price"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Naam & Prijs</h3>
        <p>
          Geef je pakket een duidelijke naam en stel je prijs in (excl. BTW, min. €25).
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="pkg-specs"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Specificaties</h3>
        <p>
          Stel het aantal video's, lengte, levertijd, revisies en hooks in.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="pkg-included"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Inclusief items</h3>
        <p>
          Selecteer wat er bij je pakket inbegrepen is: muziek, editing, ondertiteling, etc.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="pkg-save"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Opslaan</h3>
        <p>
          Vergeet niet op te slaan! Je pakketten worden direct zichtbaar op je profiel.
        </p>
      </div>
    ),
    placement: 'top',
  },
]

// Mobiele afstelling: react-joyride gebruikt standaard een tooltip van 380px, breder dan
// een telefoonscherm. Daarom breedte begrensd op de viewport en compactere padding/knoppen
// onder 768px. De tour start op mobiel gewoon automatisch (creators werken vooral op telefoon).
const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 768
const tourWidth = () => (typeof window !== 'undefined' ? Math.min(380, window.innerWidth - 32) : 380)

const PackagesTour = forwardRef(({ hasPackages, onAddDefaultPackage }, ref) => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const compact = isMobileViewport()

  useEffect(() => {
    const done = localStorage.getItem(TOUR_DONE_KEY)
    if (!done && !hasPackages) {
      // Auto-create a default package so tour targets are visible
      if (onAddDefaultPackage) {
        onAddDefaultPackage()
      }
      // Small delay to let DOM render the package card
      const timer = setTimeout(() => {
        setRun(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallback = useCallback((data) => {
    const { action, index, status, type } = data

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false)
      setStepIndex(0)
      localStorage.setItem(TOUR_DONE_KEY, 'true')
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1))
    }
  }, [])

  const restartTour = useCallback(() => {
    setStepIndex(0)
    if (!hasPackages && onAddDefaultPackage) {
      onAddDefaultPackage()
    }
    setTimeout(() => setRun(true), 300)
  }, [hasPackages, onAddDefaultPackage])

  useImperativeHandle(ref, () => ({ restartTour }), [restartTour])

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={run}
      continuous
      showProgress
      showSkipButton
      scrollOffset={80}
      spotlightPadding={8}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#0ea5e9',
          zIndex: 10000,
          width: tourWidth(),
        },
        tooltip: {
          borderRadius: '12px',
          padding: compact ? '16px' : '24px',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0ea5e9',
          borderRadius: '8px',
          padding: compact ? '10px 14px' : '10px 20px',
          fontSize: compact ? '14px' : '16px',
          fontWeight: 'bold',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: '10px',
        },
        buttonSkip: {
          color: '#9ca3af',
        },
      }}
      locale={{
        back: 'Vorige',
        close: 'Sluiten',
        last: 'Afronden',
        next: 'Volgende',
        nextLabelWithProgress: compact ? 'Volgende ({step}/{steps})' : 'Volgende ({step} van {steps})',
        skip: 'Overslaan',
      }}
    />
  )
})

export default PackagesTour
